<template>
  <div class="f-main bgImg container quiz-results-page">
    <div class="box" :style="wrapperStyle" id="box">
      <!-- 顶部紫色横幅：标题、统计、PDF / Home、面包屑 -->
      <header class="results-hero">
        <div class="results-hero-top">
          <div class="results-hero-left">
            <div class="results-pills">
              <span class="pill pill-grade">
                {{ $t("studentQuiz.Grade") }} {{ $route.query.gradeId || "—" }}
              </span>
              <span class="pill pill-soft">{{ $t("studentQuiz.badgeProgressReport") }}</span>
            </div>
            <h1 class="results-hero-title">
              {{ $t("studentQuiz.Report") }}{{ displayName }}
            </h1>
            <p v-if="lastUpdatedText" class="results-hero-meta">
              {{ $t("studentQuiz.lastUpdated", { time: lastUpdatedText }) }}
            </p>
          </div>
          <div class="results-hero-stats" v-if="hasAnyScore">
            <div class="hero-stat">
              <span class="hero-stat-num">{{ reportStats.strong }}</span>
              <span class="hero-stat-label">{{ $t("studentQuiz.statStrong") }}</span>
            </div>
            <div class="hero-stat-divider" />
            <div class="hero-stat">
              <span class="hero-stat-num">{{ reportStats.needs }}</span>
              <span class="hero-stat-label">{{ $t("studentQuiz.statNeedsWork") }}</span>
            </div>
            <div class="hero-stat-divider" />
            <div class="hero-stat">
              <span class="hero-stat-num">{{ reportStats.avg }}%</span>
              <span class="hero-stat-label">{{ $t("studentQuiz.statAvgScore") }}</span>
            </div>
          </div>
          <div class="results-hero-actions">
            <el-button id="pdf" class="btn-pdf-outline" @click="pdf">
              {{ $t("studentQuiz.PDF") }}
            </el-button>
            <button type="button" class="btn-home-orange" @click="goHome">
              <i class="el-icon-house" />
              <span>{{ $t("studentHomepage.home") }}</span>
            </button>
          </div>
        </div>
      </header>

      <div class="results-body">
        <!-- Score Overview：环形图 -->
        <section v-if="scoreRingItems.length" class="card-panel score-overview">
          <div class="panel-head">
            <div class="panel-head-icon panel-head-icon-blue">
              <i class="el-icon-data-analysis" />
            </div>
            <div class="panel-head-text">
              <h2>{{ $t("studentQuiz.scoreOverview") }}</h2>
              <p>{{ $t("studentQuiz.scoreOverviewSubtitle") }}</p>
            </div>
          </div>
          <div class="rings-row">
            <div
              v-for="row in scoreRingItems"
              :key="row.key"
              class="ring-item"
            >
              <div class="ring-wrap">
                <svg class="ring-svg" viewBox="0 0 150 150">
                  <circle
                    class="ring-track"
                    cx="75"
                    cy="75"
                    r="48"
                    fill="none"
                    stroke-width="10"
                  />
                  <circle
                    class="ring-progress"
                    cx="75"
                    cy="75"
                    r="48"
                    fill="none"
                    stroke-width="10"
                    stroke-linecap="round"
                    :stroke="row.ringColor"
                    :stroke-dasharray="ringDasharray(row.rate)"
                    transform="rotate(-90 75 75)"
                  />
                </svg>
                <span class="ring-pct" :style="{ color: row.ringColor }">{{ row.rate }}%</span>
              </div>
              <div class="ring-label">{{ row.name }}</div>
              <div v-if="row.sub" class="ring-sub">{{ row.sub }}</div>
              <span class="status-pill" :class="row.pillClass">{{ row.pillText }}</span>
            </div>
          </div>
        </section>

        <!-- Knowledge Point Scores：网格卡片 + 分析摘要 -->
        <section v-if="knowledgeCards.length" class="card-panel kp-panel">
          <div class="panel-head panel-head-split">
            <div class="panel-head-left">
              <div class="panel-head-icon panel-head-icon-pink">
                <i class="el-icon-collection-tag" />
              </div>
              <div class="panel-head-text">
                <h2>{{ $t("studentQuiz.knowledgePointScores") }}</h2>
              </div>
            </div>
            <div class="kp-legend">
              <span class="legend-dot legend-strong" /> {{ $t("studentQuiz.statStrong") }}
              <span class="legend-dot legend-needs" /> {{ $t("studentQuiz.statNeedsWork") }}
              <span class="legend-dot legend-weak" /> {{ $t("studentQuiz.weak") }}
            </div>
          </div>
          <div class="kp-grid">
            <div
              v-for="item in knowledgeCards"
              :key="item.knowledgePointId"
              class="kp-card"
              :class="item.cardTint"
            >
              <div class="kp-card-top">
                <div class="kp-card-title-row">
                  <span class="kp-icon-wrap" :class="item.iconTint">
                    <i class="el-icon-document" />
                  </span>
                  <div class="kp-names">
                    <div class="kp-name">{{ item.knowledgePointName }}</div>
                    <div v-if="categoryHint" class="kp-sub">{{ categoryHint }}</div>
                  </div>
                </div>
                <div class="kp-card-right">
                  <span class="kp-pct">{{ item.scoreRate }}%</span>
                  <span class="status-pill" :class="item.pillClass">{{ item.pillText }}</span>
                </div>
              </div>
              <div class="kp-bar-track">
                <div
                  class="kp-bar-fill"
                  :class="item.barClass"
                  :style="{ width: `${Math.min(100, Math.max(0, item.scoreRate))}%` }"
                />
              </div>
              <p class="kp-desc">{{ $t("studentQuiz.performanceLine") }}</p>
            </div>
          </div>
        </section>

        <div class="Learning" id="Learning">
          <el-button type="primary" class="btn-learning" @click="toLearning">
            {{ $t("studentQuiz.Learning") }}
          </el-button>
        </div>
      </div>
    </div>
    <div class="footer">{{ $t("common.footer") }}</div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { batchSubmit, getAnalysisReportByRecordId } from '@/api/tutor/student'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

/** 环形图周长（r=48） */
const RING_C = 2 * Math.PI * 48

export default {
  name: 'results',
  props: {
    scale: {
      type: Number,
      default: 1
    }
  },
  data () {
    return {
      quizResults: {},
      lastUpdatedText: ''
    }
  },
  computed: {
    ...mapState('student', {
      quizAnswer: (state) => state.quizAnswer
    }),
    ...mapState('user', {
      userInfo: (state) => state.userInfo
    }),
    wrapperStyle () {
      return {
        transform: `scale(${this.scale})`
      }
    },
    /**
     * 报告展示用用户名
     * @returns {string}
     */
    displayName () {
      const q = this.$route.query
      if (q.studentName) return String(q.studentName)
      if (this.userInfo && (this.userInfo.username || this.userInfo.userAccount)) {
        return this.userInfo.username || this.userInfo.userAccount
      }
      return ''
    },
    /**
     * 学科副标题（路由可选）
     * @returns {string}
     */
    categoryHint () {
      const q = this.$route.query
      const v = q.categoryName || q.categoryNameFr
      return v ? String(v) : ''
    },
    /**
     * 知识点得分列表
     * @returns {Array}
     */
    kpList () {
      return this.quizResults.knowledgePointScores || []
    },
    /**
     * 是否有任意分数（用于顶部统计）
     * @returns {boolean}
     */
    hasAnyScore () {
      const o = this.quizResults.scoreRate
      return this.kpList.length > 0 || (o != null && o !== '')
    },
    /**
     * 是否有分析摘要
     * @returns {boolean}
     */
    hasAnalysis () {
      const a = this.quizResults.knowledgePointAnalysis
      return !!(a && (a.strongSummary || a.needsImprovementSummary || a.weakSummary))
    },
    /**
     * 顶部统计：强项数、待加强数、平均分
     * @returns {{ strong: number, needs: number, weak: number, avg: number }}
     */
    reportStats () {
      const list = this.kpList
      let strong = 0
      let needs = 0
      let weak = 0
      list.forEach((item) => {
        const r = Number(item.scoreRate)
        if (r >= 80) strong++
        else if (r >= 50) needs++
        else weak++
      })
      const sum = list.reduce((s, i) => s + Number(i.scoreRate), 0)
      let avg = list.length ? Math.round(sum / list.length) : 0
      if (!list.length && this.quizResults.scoreRate != null && this.quizResults.scoreRate !== '') {
        avg = Math.round(Number(this.quizResults.scoreRate))
      }
      return { strong, needs, weak, avg }
    },
    /**
     * 环形图数据：整体 + 各知识点
     * @returns {Array<{ key: string, name: string, sub: string, rate: number, ringColor: string, pillClass: string, pillText: string }>}
     */
    scoreRingItems () {
      const items = []
      const rawOverall = this.quizResults.scoreRate
      if (rawOverall != null && rawOverall !== '') {
        const overall = Number(rawOverall)
        items.push({
          key: 'overall',
          name: this.$t('studentQuiz.overallScore'),
          sub: '',
          rate: Math.round(overall),
          ringColor: this.tierColor(overall),
          pillClass: this.tierPillClass(overall),
          pillText: this.tierLabel(overall)
        })
      }
      this.kpList.forEach((item) => {
        const r = Number(item.scoreRate)
        items.push({
          key: `kp-${item.knowledgePointId}`,
          name: item.knowledgePointName,
          sub: this.categoryHint || '',
          rate: Math.round(r),
          ringColor: this.tierColor(r),
          pillClass: this.tierPillClass(r),
          pillText: this.tierLabel(r)
        })
      })
      return items
    },
    /**
     * 知识点卡片列表（含样式类）
     * @returns {Array}
     */
    knowledgeCards () {
      return this.kpList.map((item) => {
        const r = Number(item.scoreRate)
        return {
          ...item,
          ...this.tierCardStyle(r)
        }
      })
    }
  },
  mounted () {
    this.getQuizResults()
  },
  methods: {
    /**
     * 获取报告数据：
     * - 历史报告页：按 testRecordId 调用报告接口
     * - 普通提交流程：沿用 batchSubmit
     * @returns {Promise<void>}
     */
    async getQuizResults () {
      const testRecordId = this.$route.query.testRecordId
      const isHistoricalReport = this.$route.query.type === 'historical'
      let res
      if (isHistoricalReport) {
        res = await getAnalysisReportByRecordId(testRecordId)
      } else {
        const params = this.quizAnswer[testRecordId]
        res = await batchSubmit(params)
      }
      if (res.code !== 200) {
        return
      }
      this.quizResults = isHistoricalReport
        ? this.normalizeHistoricalReportData(res.data)
        : (res.data || {})
      this.lastUpdatedText = new Date().toLocaleString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US')
    },
    /**
     * 兼容历史报告接口返回结构：
     * - 接口返回为数组，页面渲染需要对象
     * - 接口字段为 overallScore/accuracyRate，页面使用 scoreRate
     * @param {Array|Object} rawData 原始接口数据
     * @returns {Object}
     */
    normalizeHistoricalReportData (rawData) {
      const row = Array.isArray(rawData) ? (rawData[0] || {}) : (rawData || {})
      const rawScore = row.overallScore != null ? row.overallScore : row.accuracyRate
      const scoreRate = rawScore != null ? Number(rawScore) : 0
      return {
        ...row,
        scoreRate
      }
    },
    /**
     * SVG 环形进度 stroke-dasharray
     * @param {number} rate 0–100
     * @returns {string}
     */
    ringDasharray (rate) {
      const p = Math.min(100, Math.max(0, rate))
      const done = (p / 100) * RING_C
      return `${done} ${RING_C}`
    },
    /**
     * 分数档位主色
     * @param {number} scoreRate
     * @returns {string}
     */
    tierColor (scoreRate) {
      const r = Number(scoreRate)
      if (r === 100) return '#48BB78'
      if (r >= 80) return '#48BB78'
      if (r >= 50) return '#ED8936'
      if (r > 0) return '#F56565'
      return '#A0AEC0'
    },
    /**
     * 状态文案
     * @param {number} scoreRate
     * @returns {string}
     */
    tierLabel (scoreRate) {
      const r = Number(scoreRate)
      if (r >= 80) return this.$t('studentQuiz.statStrong')
      if (r >= 50) return this.$t('studentQuiz.statusGood')
      if (r > 0) return this.$t('studentQuiz.weak')
      return '—'
    },
    /**
     * 胶囊样式类
     * @param {number} scoreRate
     * @returns {string}
     */
    tierPillClass (scoreRate) {
      const r = Number(scoreRate)
      if (r >= 80) return 'pill-strong'
      if (r >= 50) return 'pill-good'
      if (r > 0) return 'pill-weak'
      return 'pill-muted'
    },
    /**
     * 卡片与进度条样式
     * @param {number} scoreRate
     * @returns {{ cardTint: string, iconTint: string, barClass: string, pillClass: string, pillText: string }}
     */
    tierCardStyle (scoreRate) {
      const r = Number(scoreRate)
      if (r >= 80) {
        return {
          cardTint: 'kp-tint-strong',
          iconTint: 'icon-strong',
          barClass: 'bar-strong',
          pillClass: 'pill-strong',
          pillText: this.$t('studentQuiz.statStrong')
        }
      }
      if (r >= 50) {
        return {
          cardTint: 'kp-tint-needs',
          iconTint: 'icon-needs',
          barClass: 'bar-needs',
          pillClass: 'pill-good',
          pillText: this.$t('studentQuiz.statusGood')
        }
      }
      return {
        cardTint: 'kp-tint-weak',
        iconTint: 'icon-weak',
        barClass: 'bar-weak',
        pillClass: 'pill-weak',
        pillText: this.$t('studentQuiz.weak')
      }
    },
    toLearning () {
      this.$router.push({
        path: '/studentLearn',
        query: {
          studentId: this.$route.query.studentId,
          gradeId: this.$route.query.gradeId,
          categoryId: this.$route.query.categoryId,
          knowledgePointId: this.$route.query.knowledgePointId,
          difficultyLevel: this.$route.query.difficultyLevel
        }
      })
    },
    /**
     * 返回学生首页
     */
    goHome () {
      const q = this.$route.query
      this.$router.push({
        path: '/studentHomepage',
        query: {
          userId: q.studentId || q.userId,
          studentName: q.studentName
        }
      })
    },
    async pdf () {
      const element = document.getElementById('box')
      const pdfElement = document.getElementById('pdf')
      const learningElement = document.getElementById('Learning')

      try {
        let wasVisible = false
        if (pdfElement) {
          wasVisible = window.getComputedStyle(pdfElement).display !== 'none'
          if (wasVisible) {
            pdfElement.style.display = 'none'
          }
        }
        if (learningElement) {
          wasVisible =
            window.getComputedStyle(learningElement).display !== 'none'
          if (wasVisible) {
            learningElement.style.display = 'none'
          }
        }

        // eslint-disable-next-line new-cap
        const pdf = new jsPDF('l', 'mm', 'a4')
        const pageWidth = pdf.internal.pageSize.getWidth()

        const originalWidth = element.scrollWidth

        const targetScale = Math.min(3, pageWidth / (originalWidth * 0.2646))
        const finalScale = Math.max(1, targetScale)

        const canvas = await html2canvas(element, {
          scale: finalScale,
          useCORS: true,
          logging: false,
          backgroundColor: '#f8f9fe'
        })

        const imgWidth = pageWidth
        const imgHeight = (canvas.height * imgWidth) / canvas.width
        const imgData = canvas.toDataURL('image/jpeg', 0.95)

        // eslint-disable-next-line new-cap
        const finalPdf = new jsPDF('l', 'mm', 'a4')
        finalPdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight)
        finalPdf.save('Quiz-Results.pdf')
      } catch (error) {
        console.error('生成PDF失败:', error)
        alert('生成PDF时发生错误，请检查控制台。')
      } finally {
        if (pdfElement) {
          pdfElement.style.display = ''
        }
        if (learningElement) {
          learningElement.style.display = ''
        }
      }
    }
  }
}
</script>

<style scoped>
.quiz-results-page.container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100% - 90px);
  background: linear-gradient(180deg, #f3e8ff 0%, #ede7f6 45%, #e8e0f5 100%);
}

.quiz-results-page .footer {
  height: 12px;
  font-weight: 400;
  font-size: 12px;
  color: #afafaf;
  position: absolute;
  bottom: 20px;
}

.quiz-results-page .box {
  width: 1443px;
  max-height: calc(100% - 160px);
  min-height: 850px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* —— Hero —— */
.results-hero {
  border-radius: 20px;
  background: linear-gradient(135deg, #5b21b6 0%, #6d28d9 50%, #7c3aed 100%);
  color: #fff;
  overflow: hidden;
}

.results-hero-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 28px 18px;
  flex-wrap: wrap;
}

.results-hero-left {
  flex: 1 1 280px;
  min-width: 0;
}

.results-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.pill {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.pill-grade {
  background: rgba(255, 255, 255, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
}

.pill-soft {
  background: rgba(255, 255, 255, 0.14);
  color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.results-hero-title {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.results-hero-meta {
  margin: 8px 0 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.78);
}

.results-hero-stats {
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
}

.hero-stat {
  text-align: center;
  padding: 0 20px;
  min-width: 88px;
}

.hero-stat-num {
  display: block;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
}

.hero-stat-label {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.82);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.hero-stat-divider {
  width: 1px;
  height: 36px;
  background: rgba(255, 255, 255, 0.28);
}

.results-hero-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.btn-pdf-outline.el-button {
  min-width: 132px;
  height: 40px;
  border-radius: 12px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.85);
  color: #fff;
  font-weight: 700;
}

.btn-pdf-outline.el-button:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: #fff;
  color: #fff;
}

.btn-home-orange {
  height: 40px;
  padding: 0 18px;
  border-radius: 12px;
  border: none;
  background: #f59e0b;
  color: #1f2937;
  font-weight: 800;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 8px 20px rgba(245, 158, 11, 0.35);
}

.btn-home-orange:hover {
  filter: brightness(1.05);
}

.results-hero-crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 28px;
  background: rgba(0, 0, 0, 0.15);
  font-size: 13px;
  color: rgba(255, 255, 255, 0.88);
}

.crumb-strong {
  font-weight: 700;
}

.crumb-sep {
  opacity: 0.65;
}

/* —— Body cards —— */
.results-body {
  padding: 24px 0 0;
  flex: 1;
  overflow-y: auto;
}

.card-panel {
  background: #fff;
  border-radius: 20px;
  padding: 24px 28px;
  margin-bottom: 20px;
  border: 1px solid rgba(226, 232, 240, 0.9);
}

.panel-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 22px;
}

.panel-head-split {
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.panel-head-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.panel-head-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #fff;
  flex-shrink: 0;
}

.panel-head-icon-blue {
  background: linear-gradient(145deg, #6366f1, #4f46e5);
}

.panel-head-icon-pink {
  background: linear-gradient(145deg, #ec4899, #db2777);
}

.panel-head-text h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #1e1b4b;
}

.panel-head-text p {
  margin: 4px 0 0;
  font-size: 14px;
  color: #64748b;
}

.kp-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.legend-strong {
  background: #48bb78;
}

.legend-needs {
  background: #ed8936;
}

.legend-weak {
  background: #f56565;
}

/* Rings */
.rings-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 28px 36px;
}

.ring-item {
  width: 140px;
  text-align: center;
}

.ring-wrap {
  position: relative;
  width: 150px;
  height: 150px;
  margin: 0 auto;
}

.ring-svg {
  width: 150px;
  height: 150px;
}

.ring-track {
  stroke: #edf2f7;
}

.ring-progress {
  transition: stroke-dasharray 0.4s ease;
}

.ring-pct {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 22px;
  font-weight: 800;
}

.ring-label {
  margin-top: 12px;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

.ring-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #94a3b8;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  margin-top: 8px;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
}

.pill-strong {
  background: rgba(72, 187, 120, 0.15);
  color: #276749;
}

.pill-good {
  background: rgba(237, 137, 54, 0.18);
  color: #c05621;
}

.pill-weak {
  background: rgba(245, 101, 101, 0.15);
  color: #c53030;
}

.pill-muted {
  background: rgba(160, 174, 192, 0.2);
  color: #4a5568;
}

/* KP grid */
.kp-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

@media (max-width: 1100px) {
  .kp-grid {
    grid-template-columns: 1fr;
  }
}

.kp-card {
  border-radius: 16px;
  padding: 18px 20px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  transition: box-shadow 0.2s ease;
}

.kp-card:hover {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.kp-tint-strong {
  background: rgba(72, 187, 120, 0.08);
}

.kp-tint-needs {
  background: rgba(237, 137, 54, 0.08);
}

.kp-tint-weak {
  background: rgba(245, 101, 101, 0.08);
}

.kp-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.kp-card-title-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
}

.kp-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  color: #fff;
}

.icon-strong {
  background: #48bb78;
}

.icon-needs {
  background: #ed8936;
}

.icon-weak {
  background: #f56565;
}

.kp-names {
  min-width: 0;
}

.kp-name {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.3;
}

.kp-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
}

.kp-card-right {
  text-align: right;
  flex-shrink: 0;
}

.kp-pct {
  display: block;
  font-size: 20px;
  font-weight: 800;
  color: #1e1b4b;
}

.kp-bar-track {
  margin-top: 14px;
  height: 6px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.25);
  overflow: hidden;
}

.kp-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.35s ease;
}

.bar-strong {
  background: #48bb78;
}

.bar-needs {
  background: #ed8936;
}

.bar-weak {
  background: #f56565;
}

.kp-desc {
  margin: 12px 0 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
}

/* Insights */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

@media (max-width: 1000px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}

.insight-card {
  border-radius: 16px;
  padding: 18px 20px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 120px;
}

.insight-strong {
  background: rgba(72, 187, 120, 0.06);
}

.insight-needs {
  background: rgba(237, 137, 54, 0.06);
}

.insight-weak {
  background: rgba(245, 101, 101, 0.06);
}

.insight-body {
  font-size: 22px;
  color: #334155;
  line-height: 1.5;
}

.insight-foot {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 18px;
}

.insight-foot img {
  width: 22px;
  height: 22px;
}

.insight-strong .insight-foot {
  color: #276749;
}

.insight-needs .insight-foot {
  color: #c05621;
}

.insight-weak .insight-foot {
  color: #c53030;
}

.Learning {
  margin: 8px 0 16px;
  display: flex;
  justify-content: center;
}

.Learning .btn-learning.el-button {
  min-width: 220px;
  height: 44px;
  border-radius: 12px;
  font-weight: 700;
  background: linear-gradient(90deg, #7c3aed 0%, #5b21b6 100%);
  border: none;
}
</style>
<style>
/* 全局样式确保导出时生效 */
.pdf-content {
  padding: 20px;
  background: white;
}
.pdf-content h1 {
  color: #333;
  font-size: 20px;
}
</style>
